import { useLazyQuery } from '@apollo/client';
import { useContext, useEffect, useState } from 'react';
import { Context } from '../../../../../context/AppContext';
import { MAX_SUGGESTIONS } from '../../../constants';
import debounce from '../../utils/debounce';
import { getNestedValue, isNotEmptyArray } from '../../utils/helpers';
import ProfileSuggestQuery from './ProfileSuggestQuery';

/**
 * Transforms fetched data object into an array of profile suggestions
 * @param {Object} data The fetched data
 * @returns {Array} Array of profile suggestions
 */
const transformData = data => {
  const edges = getNestedValue(data, 'profileSuggest', 'edges');
  if (!isNotEmptyArray(edges)) return [];
  return edges.map(({ node: { displayName, id, vanityUrlPath } }) => ({
    id,
    name: displayName,
    path: `/profile/${vanityUrlPath}`,
    suggestLabel: displayName,
  }));
};

/**
 * Logic for fetching and returning profile suggestions
 * @returns {Object} {
 * error,
 * fetchProfileSuggestions,
 * loading,
 * profileSuggestions,
 * setProfileSuggestions,
 * topProfileSuggestion,
 * }
 */
const useProfileSuggest = () => {
  const [profileSuggestions, setProfileSuggestions] = useState();
  const [topProfileSuggestion, setTopProfileSuggestion] = useState();
  const [fetch, { data, error, loading }] = useLazyQuery(ProfileSuggestQuery);
  const {
    media: { mobile },
  } = useContext(Context);

  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.POPULAR_SEARCHES
    : MAX_SUGGESTIONS.DESKTOP.POPULAR_SEARCHES;

  /**
   * Fetch the profile suggestions from the server
   * @param {string} args.query The search term to query
   * @param {Object} args.searchArea The search area to search within
   * @param {bool} args.debounceFetch Should the fetch be debounced? Defaults to true
   * @returns {void}
   */
  const fetchProfileSuggestions = ({
    query,
    searchArea,
    debounceFetch = true,
  }) => {
    const fetchParams = {
      variables: {
        query,
        first: maxSuggestions,
        ...(searchArea
          ? { statisticalAreaIds: searchArea.statisticalAreaIds }
          : {}),
      },
    };
    if (debounceFetch) debounce(() => fetch(fetchParams));
    else fetch(fetchParams);
  };

  /**
   * Update the profile suggestions whenever the fetched data is changed
   */
  useEffect(() => {
    if (!loading) {
      const hasSuggestions = isNotEmptyArray(
        getNestedValue(data, 'profileSuggest', 'edges')
      );
      if (hasSuggestions) {
        setProfileSuggestions(transformData(data));
      } else {
        setProfileSuggestions();
      }
    }
  }, [data, loading]);

  /**
   * Update the top profile suggestion whenever the profile suggestions are changed
   */
  useEffect(() => {
    if (isNotEmptyArray(profileSuggestions)) {
      setTopProfileSuggestion(profileSuggestions[0]);
    }
  }, [profileSuggestions]);

  return {
    error,
    fetchProfileSuggestions,
    loading,
    profileSuggestions,
    setProfileSuggestions,
    topProfileSuggestion,
  };
};

export default useProfileSuggest;
